import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  GetReportsListByOrgQueryParams,
  GetReportsListByPGQueryParams,
  GetReportsListByUserQueryParams,
  GetReportsListResponse,
  GetUnitRosterLiteResponse,
  PostSavePersonSelectionResponse,
} from '@appTypes/webscript';
import { AdventureSubUnitLeaderSignature } from '@appTypes/webscript/SBPurchaseOrderAPI/GetPocketAdventureCertPDF';
import { MBSubUnitLeaderSignature } from '@appTypes/webscript/SBPurchaseOrderAPI/GetPocketMBCertPDF';
import { PocketSubUnitLeaderSignature } from '@appTypes/webscript/SBPurchaseOrderAPI/GetSKUCardPDF';
import { REPORTS_ALT_BASE_URL, getArnicaRootUrl } from '@config/config';
import {
  basicReportsError,
  basicReportsResponse,
} from '@modules/reports/basicReports';
import {
  type NormalizedReportItem,
  processResponse,
} from '@modules/reports/basicReports/duck/utils';
import tagFactory from '@modules/rtk/utils/tagFactory';

import { tagTypes } from './constants';

const ONE_HOUR = 60 * 60;

export const webscriptApi = createApi({
  reducerPath: 'webscript',
  tagTypes: Object.keys(tagTypes),
  baseQuery: fetchBaseQuery({
    baseUrl: getArnicaRootUrl(),
    credentials: 'include',
    prepareHeaders: (headers, api) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const jwt = (api.getState() as any).user?.loginData?.token;
      if (jwt) {
        headers.set('Authorization', `Bearer ${jwt}`);
      }
      return headers;
    },
    validateStatus: (response, body) => {
      const httpStatusOK = response.status >= 200 && response.status <= 299;
      // This is usually returned ad `'1'` by Arnica APIs
      const apiResponseCode = body.ResponseCode || body.response_code;
      const codeOK =
        apiResponseCode && (apiResponseCode === '1' || +apiResponseCode === 1);
      return httpStatusOK && codeOK;
    },
  }),
  endpoints: builder => ({
    GetReportListByOrg: builder.query<
      {
        reports: NormalizedReportItem[];
        organizationGuid: string;
      },
      { params: GetReportsListByOrgQueryParams }
    >({
      providesTags: (_result, error, arg) => {
        if (error) return [];
        return [
          {
            type: tagTypes.ReportsList,
            id: arg.params.OrganizationGUID,
          },
        ];
      },
      query: arg => ({
        url: '',
        params: {
          Method: 'ExecuteContainerAPI',
          Container: 'SBLReportMenu',
          API: 'GetReportListByOrg',
          ...arg.params,
        },
      }),
      transformResponse: (result: GetReportsListResponse, _meta, arg) => ({
        reports: processResponse(result),
        organizationGuid: arg.params.OrganizationGUID,
      }),
      async onQueryStarted({ params }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(basicReportsResponse(data));
        } catch (err) {
          dispatch(
            basicReportsError({
              error: err,
              organizationGuid: params.OrganizationGUID,
            }),
          );
        }
      },
    }),

    GetReportListByUser: builder.query<
      {
        reports: NormalizedReportItem[];
        personGuid: string;
      },
      { params: GetReportsListByUserQueryParams }
    >({
      providesTags: (_result, error, arg) => {
        if (error) return [];
        return [
          {
            type: tagTypes.ReportsList,
            id: arg.params.PersonGUID,
          },
        ];
      },
      query: arg => ({
        url: '',
        params: {
          Method: 'ExecuteContainerAPI',
          Container: 'SBLReportMenu',
          API: 'GetReportListByUser',
          ...arg.params,
        },
      }),
      transformResponse: (result: GetReportsListResponse, _meta, arg) => ({
        reports: processResponse(result),
        personGuid: arg.params.PersonGUID,
      }),
      async onQueryStarted({ params }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(basicReportsResponse(data));
        } catch (err) {
          dispatch(
            basicReportsError({
              error: err,
              organizationGuid: params.PersonGUID,
            }),
          );
        }
      },
    }),

    GetReportListByPG: builder.query<
      {
        reports: NormalizedReportItem[];
        personGuid: string;
      },
      { params: GetReportsListByPGQueryParams }
    >({
      providesTags: (_result, error, arg) => {
        if (error) return [];
        return [
          {
            type: tagTypes.ReportsList,
            id: arg.params.PersonGUID,
          },
        ];
      },
      query: () => ({
        url: '',
        params: {
          Method: 'ExecuteContainerAPI',
          Container: 'SBLReportMenu',
          API: 'GetReportListByPG',
        },
      }),
      transformResponse: (result: GetReportsListResponse, _meta, arg) => ({
        reports: processResponse(result),
        personGuid: arg.params.PersonGUID,
      }),
      async onQueryStarted({ params }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(basicReportsResponse(data));
        } catch (err) {
          dispatch(
            basicReportsError({
              error: err,
              organizationGuid: params.PersonGUID,
            }),
          );
        }
      },
    }),

    generateClassWorksheet: builder.mutation<
      Blob,
      { memberIds: number[]; meritBadgeId: string; organizationGuid?: string }
    >({
      queryFn: async (arg, _api, _opts, baseQuery) => {
        // Step 1: Get PersonListContextToken
        const tokenBody = new URLSearchParams();
        tokenBody.append('MemberIDList', arg.memberIds.join(','));
        const tokenResult = await baseQuery({
          url: '',
          method: 'POST',
          params: {
            Method: 'ExecuteContainerAPI',
            Container: 'SBL_MB_Scout_Matrix',
            API: 'SavePersonSelection',
          },
          body: tokenBody,
        });
        if (tokenResult.error) return { error: tokenResult.error };
        const { PersonListContextToken } =
          tokenResult.data as PostSavePersonSelectionResponse;

        const payload = {
          Method: 'ExecuteContainerAPI',
          Container: 'SBL_MB_Scout_Matrix',
          API: 'GeneratePDF',
          MeritBadgeID: arg.meritBadgeId,
          PersonListContextToken,
        };
        // Step 2: Generate PDF
        const pdfResult = await baseQuery({
          url: '',
          params: payload,
          validateStatus: response =>
            response.status === 200 &&
            response.headers
              .get('content-type')
              ?.includes('application/pdf') === true,
          responseHandler: async response => await response.blob(),
        });
        if (pdfResult.error) return { error: pdfResult.error };

        return { data: pdfResult.data as Blob };
      },
    }),

    getUnitRosterLiteReport: builder.query<
      GetUnitRosterLiteResponse,
      { organizationGuid: string }
    >({
      providesTags: (result, error, arg) => {
        if (error || !result) return [];
        return [
          tagFactory.UnitRosterLiteReport.unitRoster(arg.organizationGuid),
          // future-proof in case we want to invalidate this via regular roster
          tagFactory.OrganizationRoster.organizationRosters(
            arg.organizationGuid,
          ),
        ];
      },
      keepUnusedDataFor: ONE_HOUR,
      query: ({ organizationGuid }) => ({
        // Absolute URL: the report runs against REPORTS_ALT_BASE_URL, not the Arnica proxy this api is based on
        url: `${REPORTS_ALT_BASE_URL}/advancements/webscript/output/sbreports.aspx`,
        params: {
          Method: 'ExecuteScriptSet',
          ScriptSetCode: 'SBLUnitRosterLiteReport',
          ScriptCode: 'PrintJSON',
          OrganizationGUID: organizationGuid,
        },
      }),
    }),

    postPocketCertificate: builder.query<
      Blob,
      {
        url: string;
        body:
          | AdventureSubUnitLeaderSignature[]
          | MBSubUnitLeaderSignature[]
          | PocketSubUnitLeaderSignature[];
      }
    >({
      query: arg => ({
        url: arg.url,
        method: 'POST',
        body: arg.body,
        validateStatus(response) {
          return (
            response.status === 200 &&
            response.headers.get('content-type') === 'application/pdf'
          );
        },
        responseHandler: async response => await response.blob(),
      }),
    }),
  }),
});

export const webscriptEndpoints = webscriptApi.endpoints;

export const {
  useGetReportListByOrgQuery,
  useLazyGetReportListByOrgQuery,
  useGetReportListByUserQuery,
  useGetReportListByPGQuery,
  /** used for parent/youth users */
  useGetUnitRosterLiteReportQuery,
  useLazyGetUnitRosterLiteReportQuery,
  useLazyPostPocketCertificateQuery,
  useGenerateClassWorksheetMutation,
} = webscriptApi;
